import PageTitle from 'components/common/PageTitle';
import { useMemo } from 'react';
import { Formik, Field, Form } from 'formik';
import { Grid, Row, Col } from 'react-flexbox-grid';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';
import { nullTypeError } from 'utils/yup';
import Button from 'components/common/Button';
import TextInput from 'components/inputs/TextInput';
import Select from 'components/inputs/Select';
import useGetLoanConsultants from 'hooks/loan-consultant/useGetLoanConsultants';
import useInviteUser from 'hooks/loan-consultant/useInviteUser';
import { ArrowForward } from 'assets/icons';
import { QuickCalculatorStep4 } from 'assets/icons/QuickCalculator';
import TextArea from 'components/inputs/TextArea';
import requireValue from 'utils/requireValue';
import s from './InviteUser.module.scss';

interface IInviteUserFormValues {
  email: string;
  firstName: string;
  lastName: string;
  loan_consultant_id: string;
  message: string;
}

const schema = yup.object().shape({
  email: yup
    .string()
    .email()
    .required(() => <FormattedMessage id="errors.fill_field" />)
    .nonNullable(nullTypeError('string')),
  firstName: yup
    .string()
    .required(() => <FormattedMessage id="errors.fill_field" />)
    .nonNullable(nullTypeError('string')),
  lastName: yup
    .string()
    .required(() => <FormattedMessage id="errors.fill_field" />)
    .nonNullable(nullTypeError('string')),
  loan_consultant_id: yup
    .string()
    .nullable()
    .required(() => <FormattedMessage id="errors.choose_one" />),
});

const InviteUser = () => {
  const { formatMessage: t } = useIntl();
  const onSubmit = useInviteUser();
  const [loanConsultants] = useGetLoanConsultants();

  const initialValues: IInviteUserFormValues = {
    email: '',
    firstName: '',
    lastName: '',
    loan_consultant_id: '',
    message: '',
  };

  const memoLoanConsultants = useMemo(
    () =>
      // A missing list, consultant, profile or email threw here, as it did.
      requireValue(loanConsultants, 'map').map((loanConsultant) => {
        const { _id, emails, profile } = requireValue(loanConsultant, '_id');
        const { firstName, lastName } = requireValue(profile, 'firstName');
        const { address } = requireValue(requireValue(emails, '0')[0], 'address');
        return {
          label: `${String(firstName)} ${String(lastName)} - ${String(address)}`,
          value: _id,
        };
      }),
    [loanConsultants],
  );

  return (
    <Grid>
      <Row center="md" between="lg">
        <Col md={9} lg={12}>
          <PageTitle>{t({ id: 'page_titles.invite_user' })}</PageTitle>
        </Col>
      </Row>
      <Row center="md" between="lg">
        <Col md={9} lg={6}>
          <Formik<IInviteUserFormValues>
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={onSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <Field
                  type="email"
                  name="email"
                  lowerCase
                  component={TextInput}
                  required
                  label={t({ id: 'auth.email' })}
                />
                <Field
                  type="text"
                  name="firstName"
                  component={TextInput}
                  label={t({ id: 'user.first_name' })}
                />
                <Field
                  type="text"
                  name="lastName"
                  component={TextInput}
                  label={t({ id: 'user.last_name' })}
                />
                <Field
                  name="loan_consultant_id"
                  component={Select}
                  required
                  label={t({ id: `invite_user.loan_consultant` })}
                  options={memoLoanConsultants}
                />
                <Field
                  name="message"
                  component={TextArea}
                  label={t({ id: 'invite_user.message' })}
                  minRows={7}
                />

                <div className={s.btnWrapper}>
                  <Button
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    color="primary"
                    type="submit"
                    endIcon={<ArrowForward />}
                  >
                    {t({ id: 'invite_user.add_user_btn' })}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Col>
        <Col md={0} lg={6} xl={6} first="xs" last="md">
          <QuickCalculatorStep4 title={t({ id: 'alt_text.invite_user_page_img' })} />
        </Col>
      </Row>
    </Grid>
  );
};

export default InviteUser;
